import { useCallback } from 'react';
import { useTheme } from 'styled-components';

import { StrongText, StyledFlex } from '@dreryk/react-components';
import { Translate } from '@dreryk/react-common';

import { translationKeys } from '../translations';
import { PausingIcon } from '../icons/PausingIcon';
import { useAiAssistantRecordingGlobalState } from '../shared/useAiAssistantRecordingGlobalState';
import { ensureMicrophoneAccess } from '../audio/ensureMicrophoneAccess';
import { useAiAssistantTokensQuotaGuard } from '../shared/useAiAssistantTokensQuotaGuard';
import { DiscardIcon } from '../icons/DiscardIcon';
import { ResumeIcon } from '../icons/ResumeIcon';

import { formatElapsedTime } from './formatElapsedTime';
import { AiAssistantMainMenuTileItemContainer } from './AiAssistantMainMenuTileItemContainer';
import { TileItemPrimaryButton } from './TileItemPrimaryButton';

const t = translationKeys.mainMenuTileItem;

type AiAssistantMainMenuTileItemPausedProps = {
   accumulatedSeconds: number;
   onStartTranscription: () => Promise<boolean>;
   onSummarizeTranscript: VoidFunction;
   onDiscardTranscript: VoidFunction;
};

export const AiAssistantMainMenuTileItemPaused = (props: AiAssistantMainMenuTileItemPausedProps): JSX.Element => {
   const theme = useTheme();
   const { resumeRecording, generateNote, reset } = useAiAssistantRecordingGlobalState();
   const verifyTokensQuota = useAiAssistantTokensQuotaGuard();

   const handleResume = useCallback(async () => {
      if (!(await verifyTokensQuota()) || !(await ensureMicrophoneAccess())) {
         return;
      }

      if (await props.onStartTranscription()) {
         resumeRecording();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.onStartTranscription, resumeRecording, verifyTokensQuota]);

   const handleDiscard = useCallback(() => {
      props.onDiscardTranscript();
      reset();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.onDiscardTranscript, reset]);

   const handleGenerateNote = useCallback(() => {
      generateNote();
      props.onSummarizeTranscript();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [generateNote, props.onSummarizeTranscript]);

   return (
      <AiAssistantMainMenuTileItemContainer icon={<PausingIcon />} backgroundColor={theme.backgroundBody}>
         <StyledFlex direction={'column'} gap={8} alignItems={'center'} width={'100%'}>
            <StyledFlex direction={'column'} gap={4}>
               <StyledFlex gap={8} alignItems={'flex-end'}>
                  <DiscardIcon onClick={handleDiscard} />
                  <PausingIcon />
                  <ResumeIcon onClick={handleResume} />
               </StyledFlex>
               <StyledFlex direction={'column'} gap={4} alignItems={'center'}>
                  <StrongText testId={'elapsedTime'} color={theme.borderDarker}>
                     <Translate>{formatElapsedTime(props.accumulatedSeconds)}</Translate>
                  </StrongText>
               </StyledFlex>
            </StyledFlex>
            <TileItemPrimaryButton label={t.generateNote} testId={'generateNote'} onClick={handleGenerateNote} />
         </StyledFlex>
      </AiAssistantMainMenuTileItemContainer>
   );
};
